import type { StateCreator } from 'zustand';
import { ApiClientError } from '@/shared/lib/api';
import { authLogger } from '@/shared/lib/logger';
import { AuthInitializer } from '@/shared/lib/auth-initializer';
import { AuthTokenService } from '@/shared/lib/auth-token-service';
import { cacheInvalidation, cacheKeys } from '@/shared/lib/cache-invalidation';
import { queryClient } from '@/shared/lib/query-client';
import { authService } from '@/features/auth/api/auth';
import { profileService } from '@/shared/api/services/profile.service';
import { AuthErrorHandler } from '@/shared/lib/auth-error-handler';
import { goalsAutosaveCoordinator } from '@/features/project/lib/goals-autosave-coordinator';
import type { User } from '@/types/user';
import type {
  LoginRequest,
  RegisterRequest,
  ResetPasswordRequest,
} from '@/types/auth';
import { WebSocketAuthEvents } from '../lib/websocket-auth-events';
import {
  type AuthData,
  getCachedUser,
  initialAuthData,
  setCachedUser,
  wipePersistedCachesIfIdentityChanged,
} from './auth.store.internals';

/**
 * Async pipelines + their cross-cutting helpers (`reset`, `clearErrors`,
 * `initializeAuth`). Lives in its own file because the action bodies dwarf
 * the data slice — keeping them out of `auth.store.ts` lets the store entry
 * stay a thin composition root.
 *
 * Each action freely writes both Identity and Flow fields (e.g. login() sets
 * `loginError + isLoggingIn` together with `token + isAuthenticated`); a
 * harder split into per-area sub-slices was tried and discarded — there is
 * no cohesive sub-family of actions that doesn't touch all of these fields.
 */
export interface AuthActions {
  login: (credentials: LoginRequest) => Promise<void>;
  register: (credentials: RegisterRequest) => Promise<void>;
  logout: (skipRedirect?: boolean) => Promise<void>;
  refreshUser: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (data: ResetPasswordRequest) => Promise<void>;
  activateAccount: (data: {
    email: string;
    activation_token: string;
    password: string;
  }) => Promise<void>;
  initializeAuth: () => Promise<void>;
  getGoogleOAuthUrl: () => Promise<string>;
  sendVerificationCode: () => Promise<void>;
  verifyEmailCode: (
    code: string
  ) => Promise<{ defaultProjectId: string | null }>;
  clearErrors: () => void;
  reset: () => void;
}

type AuthStore = AuthData & AuthActions;

export const createActions: StateCreator<AuthStore, [], [], AuthActions> = (
  set,
  get
) => ({
  initializeAuth: async () => {
    try {
      const result = await AuthInitializer.initialize();

      // `validateToken` inside AuthInitializer already populated
      // cacheKeys.user via profileService.applyProfileData (richer payload
      // than the slim User returned here). Only seed the cache when
      // AuthInitializer didn't — e.g. dev auto-login that bypasses the
      // /user fetch.
      if (result.user && !getCachedUser()) {
        setCachedUser(result.user);
      } else if (!result.user) {
        setCachedUser(null);
      }
      set({
        token: result.token,
        isAuthenticated: result.isAuthenticated,
        isInitialized: result.isInitialized,
        pendingEmailVerification: result.pendingEmailVerification || false,
      });

      // Skip if email not verified — Ably auth returns 403.
      if (
        result.isAuthenticated &&
        result.user &&
        result.token &&
        !result.pendingEmailVerification
      ) {
        WebSocketAuthEvents.onAuthInitialized(result.user.id, result.token);
      }
    } catch (error) {
      authLogger.error('Auth initialization error:', error);
      set({
        isAuthenticated: false,
        isInitialized: true,
      });
    }
  },

  login: async (credentials) => {
    try {
      set({ loginError: null, isLoggingIn: true });

      const authData = await authService.login(credentials);

      // Evict persisted cache BEFORE we overwrite the stored user — otherwise
      // `wipePersistedCachesIfIdentityChanged` can't compare against the prior id.
      await wipePersistedCachesIfIdentityChanged(authData.user.id);

      await AuthTokenService.setToken(authData.token);
      await AuthTokenService.setUser(authData.user);

      authLogger.info('Login successful');
      const needsVerification = !authData.user.email_verified_at;
      setCachedUser(authData.user);
      set({
        token: authData.token,
        isAuthenticated: true,
        isLoggingIn: false,
        pendingEmailVerification: needsVerification,
      });

      AuthTokenService.broadcastLogin();

      cacheInvalidation.afterLogin();

      if (!needsVerification) {
        WebSocketAuthEvents.onLogin(authData.user.id, authData.token);
      }

      // Push guest-picked locale from the login screen into the user's profile.
      // Without this, the UI would flip from the guest's choice to the server
      // value right after login.
      void import('@/features/i18n').then(({ syncGuestLocaleAfterLogin }) =>
        syncGuestLocaleAfterLogin(authData.user.locale)
      );

      // Auth response now ships the full UserProfileResource, so we can seed
      // the subscription store + token balance straight from it — no second
      // /user round-trip, no race window where the render-plan gate sees a
      // stub. Same pipeline /user uses on refreshUser.
      try {
        await profileService.applyProfileData(authData.profileData, 'login');
      } catch (seedError) {
        authLogger.error('Failed to seed profile after login:', seedError);
        // Don't throw — login itself succeeded.
      }
    } catch (error) {
      let errorMessage = 'Login failed';
      if (error instanceof ApiClientError) {
        errorMessage = error.getFirstError();
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      set({
        isLoggingIn: false,
        loginError: errorMessage,
      });

      if (error instanceof Error) {
        AuthErrorHandler.handleAuthError(error, { context: 'login' });
      }
      throw error;
    }
  },

  register: async (credentials) => {
    try {
      set({ registerError: null, isRegistering: true });

      const authData = await authService.register(credentials);

      // See login() — same greenfield-prod guard. A re-registered email gets
      // a fresh user_id, and register() was previously missing this wipe.
      await wipePersistedCachesIfIdentityChanged(authData.user.id);

      await AuthTokenService.setToken(authData.token);
      await AuthTokenService.setUser(authData.user);

      // Drop any in-flight/persisted queries left over from a previous
      // unauthenticated browsing or a crashed session.
      cacheInvalidation.afterLogin();

      const needsVerification = !authData.user.email_verified_at;
      setCachedUser(authData.user);
      set({
        token: authData.token,
        isAuthenticated: true,
        isRegistering: false,
        pendingEmailVerification: needsVerification,
      });

      AuthTokenService.broadcastLogin();

      if (!needsVerification) {
        WebSocketAuthEvents.onLogin(authData.user.id, authData.token);
      }

      // Push guest-picked locale from the register screen into the user's profile.
      void import('@/features/i18n').then(({ syncGuestLocaleAfterLogin }) =>
        syncGuestLocaleAfterLogin(authData.user.locale)
      );

      // Same shared seeder as login(): auth response carries the full
      // UserProfileResource, so subscription store + token balance get
      // populated synchronously without a follow-up /user fetch.
      try {
        await profileService.applyProfileData(authData.profileData, 'register');
      } catch (seedError) {
        authLogger.error('Failed to seed profile after register:', seedError);
      }
    } catch (error) {
      let errorMessage = 'Registration failed';
      if (error instanceof ApiClientError) {
        errorMessage = error.getFirstError();
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      set({
        isRegistering: false,
        registerError: errorMessage,
      });

      if (error instanceof Error) {
        AuthErrorHandler.handleAuthError(error, { context: 'register' });
      }
      throw error;
    }
  },

  logout: async (skipRedirect = false) => {
    // Capture before state-clear: afterLogout needs the previous user id to
    // wipe their userId-scoped localStorage bucket (drafts/album/preferences).
    const previousUserId = getCachedUser()?.id ?? null;
    // Cancel any debounced goals PATCH BEFORE the network logout — the 800 ms
    // autosave debounce would otherwise fire mid-logout and either race a
    // now-revoked token (401) or land a write under the previous identity.
    // Apple/Android avoid this by tearing down their goals coordinators on
    // auth-change; web had no equivalent until W3.2.
    goalsAutosaveCoordinator.cancelAll();
    try {
      set({ isLoggingOut: true });

      // Broadcast logout event to other tabs BEFORE calling API. Only if not
      // skipping redirect (silent logout for user switching).
      if (!skipRedirect) {
        AuthTokenService.broadcastLogout();
      }

      await authService.logout();
    } catch (error) {
      authLogger.error('Logout error:', error);
    } finally {
      // 1. Update auth state immediately (prevents any reconnection attempts)
      setCachedUser(null);
      set({
        token: null,
        isAuthenticated: false,
        isLoggingOut: false,
        pendingEmailVerification: false,
      });

      // 2. Clear auth tokens (prevents WebSocket reconnection with old token)
      await AuthTokenService.clearAuth();

      // 3. Disconnect WebSocket (no token to reconnect with now)
      WebSocketAuthEvents.onLogout();

      // 4. Clear TanStack Query cache (prevents stale data from previous user)
      try {
        await cacheInvalidation.afterLogout(previousUserId);
      } catch (cacheError) {
        authLogger.error('Error clearing query cache:', cacheError);
      }

      // 5. Reset all stores (safe now - no auth to trigger reconnections).
      // Subscription server-state lives in the Query cache; `cacheInvalidation
      // .afterLogout` above already cleared queryClient — explicitly drop
      // balance + active-subscription keys for clarity, then reset the
      // client-only notifications + job queue Zustand stores.
      try {
        queryClient.removeQueries({ queryKey: cacheKeys.balance });
        queryClient.removeQueries({
          queryKey: cacheKeys.subscriptions.active,
        });

        const { useSubscriptionNotificationsStore } = await import(
          '@/features/subscription/model/subscription-notifications.store'
        );
        useSubscriptionNotificationsStore.getState().reset();

        const { useJobQueueStore } = await import(
          '@/features/design/job-queue/model/job-queue.store'
        );
        useJobQueueStore.getState().reset();

        authLogger.debug('All stores reset successfully');
      } catch (storeError) {
        authLogger.error('Error resetting stores:', storeError);
      }

      // 6. Small delay to ensure all cleanup is done
      await new Promise((resolve) => setTimeout(resolve, 50));

      // 7. Redirect to login page (use replace to prevent back button issues).
      // Skip redirect if explicitly requested (e.g., user switching in checkout).
      if (!skipRedirect) {
        authLogger.info('Logout complete, redirecting to login');
        window.location.replace('/auth/login');
      } else {
        authLogger.info('Logout complete, skipping redirect (silent logout)');
      }
    }
  },

  refreshUser: async () => {
    try {
      set({ isRefreshingUser: true, refreshUserError: null });

      const profileData = await profileService.fetchProfile();

      if (!profileData.user) {
        throw new Error('User data missing from profile response');
      }

      const currentUser: User = {
        id: profileData.user.id,
        email: profileData.user.email,
        email_verified_at: profileData.user.email_verified_at || null,
        created_at: profileData.user.created_at,
        updated_at:
          profileData.user.updated_at || profileData.user.created_at,
        has_completed_renders: profileData.user.has_completed_renders,
        should_show_onboarding: profileData.user.should_show_onboarding,
      };

      // Detect silent account switch (e.g. API returns a different user for
      // this token after a server-side reset). Apple uses the same guard in
      // AuthManager.refreshUser(); without it, cached project/subscription
      // queries keep the prior id until the next login/logout cycle.
      const previousUserId = getCachedUser()?.id;
      if (previousUserId && previousUserId !== currentUser.id) {
        authLogger.warn(
          `refreshUser: silent identity switch (${previousUserId} -> ${currentUser.id}), wiping caches`
        );
        await cacheInvalidation.afterLogout(previousUserId);
      }

      await AuthTokenService.setUser(currentUser);

      // Single seeding pipeline shared with auth flows — same payload from
      // /user and login/register/google emit identical profileData, so this
      // call covers User + subscription store + token balance for both paths.
      await profileService.applyProfileData(profileData, 'auth-refresh');

      set({ isRefreshingUser: false });

      authLogger.debug('User profile refreshed successfully');
    } catch (error) {
      authLogger.error('Refresh user error:', error);

      if (error instanceof ApiClientError && error.status === 401) {
        await get().logout();
      } else {
        const errorMessage =
          error instanceof ApiClientError
            ? error.getFirstError()
            : 'Failed to refresh user data';

        set({
          refreshUserError: errorMessage,
          isRefreshingUser: false,
        });
      }
    }
  },

  forgotPassword: async (email: string) => {
    try {
      set({ isSendingPasswordReset: true, forgotPasswordError: null });

      await authService.forgotPassword({ email });

      set({ isSendingPasswordReset: false });
    } catch (error) {
      const errorMessage =
        error instanceof ApiClientError
          ? error.getFirstError() || 'Failed to send password reset email'
          : 'An unexpected error occurred';

      set({
        forgotPasswordError: errorMessage,
        isSendingPasswordReset: false,
      });
      throw error;
    }
  },

  resetPassword: async (data) => {
    try {
      set({ isResettingPassword: true, resetPasswordError: null });

      const authData = await authService.resetPassword(data);

      // Same identity-change wipe as login()/register() — resetPassword also
      // lands the user in an authenticated session and was previously missing
      // both the identity guard and the afterLogin invalidation.
      await wipePersistedCachesIfIdentityChanged(authData.user.id);

      await AuthTokenService.setToken(authData.token);
      await AuthTokenService.setUser(authData.user);

      cacheInvalidation.afterLogin();

      setCachedUser(authData.user);
      set({
        token: authData.token,
        isAuthenticated: true,
        isResettingPassword: false,
      });

      AuthTokenService.broadcastLogin();

      WebSocketAuthEvents.onLogin(authData.user.id, authData.token);

      // Push guest-picked locale into the user's profile after password reset.
      void import('@/features/i18n').then(({ syncGuestLocaleAfterLogin }) =>
        syncGuestLocaleAfterLogin(authData.user.locale)
      );

      // Same shared seeder as login()/register(): full UserProfileResource
      // payload populates subscription store + token balance synchronously.
      try {
        await profileService.applyProfileData(
          authData.profileData,
          'reset-password'
        );
      } catch (seedError) {
        authLogger.error('Failed to seed profile after reset:', seedError);
      }
    } catch (error) {
      const errorMessage =
        error instanceof ApiClientError
          ? error.getFirstError() || 'Failed to reset password'
          : 'An unexpected error occurred';

      set({
        resetPasswordError: errorMessage,
        isResettingPassword: false,
      });
      throw error;
    }
  },

  activateAccount: async (data) => {
    const authData = await authService.activateAccount(data);

    // Same identity-change wipe as login()/register()/resetPassword(): an
    // activation lands the user in an authenticated session, and the prior
    // session in this browser may belong to a different account.
    await wipePersistedCachesIfIdentityChanged(authData.user.id);

    await AuthTokenService.setToken(authData.token);
    await AuthTokenService.setUser(authData.user);

    cacheInvalidation.afterLogin();

    setCachedUser(authData.user);
    set({
      token: authData.token,
      isAuthenticated: true,
      pendingEmailVerification: false,
    });

    AuthTokenService.broadcastLogin();
    WebSocketAuthEvents.onLogin(authData.user.id, authData.token);

    // Same shared seeder as login()/register()/resetPassword(): the auth
    // response carries the full UserProfileResource, so subscription store +
    // token balance get populated synchronously without a follow-up /user
    // fetch.
    try {
      await profileService.applyProfileData(
        authData.profileData,
        'activate-account'
      );
    } catch (seedError) {
      authLogger.error('Failed to seed profile after activate:', seedError);
    }
  },

  sendVerificationCode: async () => {
    await authService.sendVerificationCode();
  },

  verifyEmailCode: async (code: string) => {
    const { user, profileData, defaultProjectId } =
      await authService.verifyEmailCode(code);
    await AuthTokenService.setUser(user);
    setCachedUser(user);
    set({ pendingEmailVerification: false });

    // Connect WebSocket now that email is verified
    const { token } = get();
    if (token && user.id) {
      WebSocketAuthEvents.onLogin(user.id, token);
    }

    // Same shared seeder — verifyCode response carries the full
    // UserProfileResource, so we populate caches identically to /user refresh.
    // No second fetch needed once email is confirmed.
    try {
      await profileService.applyProfileData(profileData, 'verify-email');
    } catch (seedError) {
      authLogger.error('Failed to seed profile after verify:', seedError);
    }

    // Returned so the caller can route the user into their default project
    // (onboarding lands on /project/:id). Falls back to the `/` resolver when
    // the API didn't attach a default project — it picks the first building.
    return { defaultProjectId };
  },

  getGoogleOAuthUrl: async () => {
    const redirectUrl = await authService.getGoogleOAuthUrl();
    return redirectUrl;
  },

  clearErrors: () =>
    set({
      loginError: null,
      registerError: null,
      refreshUserError: null,
      forgotPasswordError: null,
      resetPasswordError: null,
    }),

  reset: () => {
    setCachedUser(null);
    set({
      ...initialAuthData,
      // initializeAuth() flips this when the app boots; logout/silent-reset
      // paths preserve "we've already booted" so the auth gate doesn't
      // flash back to a loading state.
      isInitialized: true,
    });
  },
});
